import { Button } from '@react-spectrum/s2';
import { useCallback, useMemo } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { EmailRecipients } from '@/components/artifact/EmailRecipients.tsx';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot.ts';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep.ts';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout.tsx';

import type { AudienceSummary } from '../../../../../bridge/types/action.ts';
import type { EmailRecipient } from '../../../../../bridge/types/artifact.ts';
import { isSingleSendableEmailProcess } from '../../../../../bridge/utils/setupCardItems.ts';

interface ProcessRecipientsPanelProps {
	processId: string;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const ProcessRecipientsPanel = ({
	processId,
	onBack,
	onClose,
	style,
}: ProcessRecipientsPanelProps) => {
	const bridgeApi = useBridgeApi();
	const goToNextItem = useGoToNextStep();
	const openChatPanel = useOpenChatPanel();
	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Recipients confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => goToNextItem(processId, `recipients:${processId}`),
	});

	useEnsureSetupSnapshot(processId);

	const snapshot =
		bridgeApi.user_state?.process_setup_snapshots?.[processId] ?? null;

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const currentChat = currentChatId
		? (bridgeApi.user_state?.chats?.[currentChatId] ?? null)
		: null;
	const linkedArtifactId =
		currentChat?.linked_item?.type === 'artifact'
			? currentChat.linked_item.id
			: null;
	const isStandaloneEdit =
		!!linkedArtifactId &&
		!!snapshot?.children.some((c) => c.id === linkedArtifactId);
	// Advance to the schedule step (with its "Send now" control) for any one-off
	// email send: a standalone edit, or a campaign whose only sendable piece is a
	// single email. A multi-piece campaign stops here and finalizes recipients;
	// its pieces are scheduled together elsewhere. Until the snapshot hydrates,
	// fall back to the multi-piece behavior.
	const advanceToSchedule =
		isStandaloneEdit ||
		(snapshot != null && isSingleSendableEmailProcess(snapshot));

	const sendActions = useMemo(
		() =>
			(snapshot?.actions ?? []).filter(
				(a) => a.type === 'send_email_to_contacts',
			),
		[snapshot?.actions],
	);

	const firstConfig = (sendActions[0]?.config ?? {}) as Record<string, unknown>;

	const recipients: EmailRecipient[] = useMemo(
		() =>
			Array.isArray(firstConfig.recipients)
				? (firstConfig.recipients as EmailRecipient[]).filter(
						(r) => r.source?.type !== 'audience',
					)
				: [],
		[firstConfig.recipients],
	);

	const audienceSummaries: AudienceSummary[] = useMemo(() => {
		if (Array.isArray(firstConfig.audience_summaries)) {
			return firstConfig.audience_summaries as AudienceSummary[];
		}
		if (Array.isArray(firstConfig.audience_ids)) {
			return (firstConfig.audience_ids as string[]).map((id) => ({
				audienceId: id,
				audienceName: id,
			}));
		}
		return [];
	}, [firstConfig.audience_ids, firstConfig.audience_summaries]);

	const patchAllSendActions = useCallback(
		(updates: Record<string, unknown>) => {
			for (const action of sendActions) {
				const existingConfig = (action.config ?? {}) as Record<string, unknown>;
				bridgeApi.sendAction({
					type: 'do_edit_process_action',
					action_id: action.id,
					updates: {
						config: { ...existingConfig, ...updates },
					},
				});
			}
		},
		[bridgeApi, sendActions],
	);

	const handleRecipientsChange = useCallback(
		(nextRecipients: EmailRecipient[]) => {
			patchAllSendActions({ recipients: nextRecipients });
		},
		[patchAllSendActions],
	);

	const handleAudienceSummariesChange = useCallback(
		(summaries: AudienceSummary[]) => {
			patchAllSendActions({
				audience_summaries: summaries,
				audience_ids: summaries.map((s) => s.audienceId),
			});
		},
		[patchAllSendActions],
	);

	const handleSubmit = useCallback(() => {
		bridgeApi.sendAction({
			type: 'do_finalize_process_recipients',
			process_id: processId,
			recipients: {
				contact_ids: [],
				audience_ids: audienceSummaries.map((s) => s.audienceId),
				audience_summaries: audienceSummaries.map((s) => ({
					audienceId: s.audienceId,
					audienceName: s.audienceName,
				})),
			},
		});
		if (advanceToSchedule) {
			openChatPanel({
				panel_type: 'process_schedule',
				item_id: processId,
			});
			return;
		}
		markSubmitted();
	}, [
		bridgeApi,
		openChatPanel,
		processId,
		audienceSummaries,
		advanceToSchedule,
		markSubmitted,
	]);

	const actionRenderer = useCallback(
		() => (
			<div className="ml-auto flex items-center gap-2">
				<Button type="button" variant="accent" onPress={handleSubmit}>
					{advanceToSchedule ? 'Next' : 'Approve'}
				</Button>
			</div>
		),
		[handleSubmit, advanceToSchedule],
	);

	return (
		<ChatPanelLayout
			title="Choose Recipients"
			description="Choose who receives every email in this campaign."
			actionRenderer={submittedView ? undefined : actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{submittedView ?? (
				<div className="flex min-h-0 flex-1 flex-col gap-3">
					<EmailRecipients
						recipients={recipients}
						audienceSummaries={audienceSummaries}
						onRecipientsChange={handleRecipientsChange}
						onAudienceSummariesChange={handleAudienceSummariesChange}
					/>
				</div>
			)}
		</ChatPanelLayout>
	);
};
